import React from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { compose } from 'recompose'
import { connect as connectRedux } from 'react-redux'
import FormsyForm from 'formsy-react'
import _get from 'lodash/get'
import _isArray from 'lodash/isArray'
import _isFunction from 'lodash/isFunction'
import ToolTipArrow, { DIRECTION_UP } from '../ToolTipArrow/index'
import connectRouter from '../Router/connect'
import { connect as connectStaticText } from '../StaticText/connect'
import { getBoundActions } from '../../actions/index'
import { SEARCH_PERFORMED_GA4_EVENT } from '../../services/event-tracking/index'
import { historyRedirect } from '../../services/navigation/index'
import SearchFormInput from '../SearchFormInput/index'
import { variationShouldRender } from '../../services/testarossa/index'
import IconV2, { ICON_TYPES } from '../Icon.v2/index'
import Button, { TYPES } from '../Button/index'

function getClassName (className, hidden) {
  const classes = ['search-form__form']
  if (hidden) {
    classes.push('search-form--visible')
  }

  if (_isArray(className)) {
    classes.push(...className)
  } else {
    classes.push(className)
  }

  return classes.join(' ')
}

function isGPSEEnabled () {
  return variationShouldRender({ campaign: 'AC-8825', variation: 1 })
}

function getFilterClassName (className, active) {
  const classes = [className]
  if (active) {
    classes.push(`${className}--active`)
  }

  return classes.join(' ')
}

class SearchForm extends React.Component {
  constructor (props) {
    super(props)

    const { staticText } = props
    this.state = {
      currentQuery: this.props.defaultSearchTerm,
      activeSort: this.props.defaultSearchSort,
      FILTERS: [
        {
          label: staticText.getIn(['data', 'relevance', 'label']),
          value: '', // passing nothing defaults the search to relevent
        },
        {
          label: staticText.getIn(['data', 'recent', 'label']),
          value: 'recent',
        },
        {
          label: staticText.getIn(['data', 'popular', 'label']),
          value: 'popular',
        },
        {
          label: staticText.getIn(['data', 'alpha', 'label']),
          value: 'alpha',
        },
      ],
      isActive: false,
    }
  }

  handleSearchQuery = (model = {}, resetForm) => {
    const { props, state } = this
    const {
      history, onSubmit, resetOnSubmit, auth, userLanguage, noSearchRedirect,
    } = props
    const { activeSort } = state
    const { q } = model
    const trimmedQuery = q != null ? String(q).trim() : ''
    if (trimmedQuery !== '') {
      this.props.setEventDefault(SEARCH_PERFORMED_GA4_EVENT.mergeDeep({
        search_query: trimmedQuery,
      }))
    }
    if (resetOnSubmit) {
      resetForm()
      document.activeElement.blur()
    }
    if (_isFunction(onSubmit)) {
      onSubmit(model)
      document.activeElement.blur()
    }
    if (noSearchRedirect) {
      return null
    }
    // Only redirect if there is a query
    if (q) {
      this.setState({
        currentQuery: q,
      })
      const query = { q }
      if (activeSort && activeSort !== '') {
        query.sort = activeSort
      }
      if (isGPSEEnabled()) {
        query.gpse = 1
      }
      historyRedirect({
        history, url: '/search', query, auth, language: userLanguage,
      })
    }
    return null
  }

  handleSortClick = (evt) => {
    const { props, state } = this
    const {
      userLanguage,
      history,
      auth,
    } = props

    const { activeSort } = state
    const value = evt.currentTarget.getAttribute('data-value')

    if (value === activeSort) return

    this.setState(() => ({
      activeSort: value,
    }))
    const currentQuery = _get(this.state, 'currentQuery')
    if (currentQuery && currentQuery !== '') {
      const query = { q: currentQuery }
      if (value && value !== '') {
        query.sort = value
      }
      if (isGPSEEnabled()) query.gpse = 1
      historyRedirect({
        language: userLanguage, url: '/search', history, query, auth,
      })
    }
  }

  renderFilters = () => {
    const { state, props, handleSortClick } = this
    const { activeSort, FILTERS } = state
    const { isNav, hideFilters } = props

    if (isNav || hideFilters) return null

    return (
      <div className="search-form__filters-container">
        <ul className="search-form__filters-list">
          {
            FILTERS.map((item) => {
              const isActive = item.value === activeSort
              return (
                <li
                  key={`search-filter-${item.label}`}
                  className={getFilterClassName('search-form__filters-list-item', isActive)}
                >
                  <a
                    className={getFilterClassName('search-form__filter-button', isActive)}
                    data-value={item.value}
                    onClick={handleSortClick}
                  >
                    {item.label}
                  </a>
                </li>
              )
            })
          }
        </ul>
        <hr />
      </div>
    )
  }

  render () {
    const {
      props,
      handleSearchQuery,
      renderFilters,
    } = this

    const {
      className,
      defaultSearchTerm,
      searchInputClass,
      placeholderText,
      autofocus,
      hasButton,
      iconClass,
      children,
      onFocus,
      onBlur,
      darkMode,
    } = props

    const searchClassName = [
      'search-form',
      darkMode ? 'search-form--dark-mode' : null,
      this.state.isActive ? 'search-form--active' : null,
    ].filter((cls) => !!cls).join(' ')

    return (
      <div className={searchClassName}>
        <FormsyForm
          className={getClassName(className)}
          onValidSubmit={handleSearchQuery}
        >
          <div className="search-form__input-wrapper">
            {hasButton ? (
              <Button type={TYPES.LINK} submit className="search-form-button">
                <IconV2
                  className={`search-form-button__icon ${iconClass || ''}`}
                  type={ICON_TYPES.SEARCH}
                />
              </Button>
            ) : null}
            <SearchFormInput
              defaultValue={defaultSearchTerm}
              name="q"
              searchInputClass={searchInputClass}
              placeholderText={placeholderText}
              autofocus={autofocus}
              onFocus={() => {
                this.setState({ isActive: true })
                if (_isFunction(onFocus)) {
                  onFocus()
                }
              }}
              onBlur={() => {
                this.setState({ isActive: false })
                if (_isFunction(onBlur)) {
                  onBlur()
                }
              }}
            />
          </div>
          {children}
        </FormsyForm>
        <ToolTipArrow
          direction={DIRECTION_UP}
          activeArrow="search-arrow"
        />
        { renderFilters() }
      </div>
    )
  }
}

SearchForm.propTypes = {
  staticText: ImmutablePropTypes.map.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  defaultSearchTerm: PropTypes.string,
  defaultSearchSort: PropTypes.string,
  history: PropTypes.object.isRequired,
  placeholderText: PropTypes.string,
  searchInputClass: PropTypes.array,
  resetOnSubmit: PropTypes.bool,
  className: PropTypes.array,
  onSubmit: PropTypes.func,
  hasButton: PropTypes.bool,
  hideFilters: PropTypes.bool,
  iconClass: PropTypes.array,
  location: PropTypes.object,
  autofocus: PropTypes.bool,
  isNav: PropTypes.bool,
  noSearchRedirect: PropTypes.bool,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  darkMode: PropTypes.bool,
  setEventDefault: PropTypes.func.isRequired,
}

export default compose(
  connectRouter(),
  connectStaticText({ storeKey: 'search' }),
  connectRedux(
    (state) => ({
      userLanguage: state.user.getIn(['data', 'language']),
      auth: state.auth,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setEventDefault: actions.eventTracking.setEventDefault,
      }
    },
  ),
)(SearchForm)
